import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../lib/api';
import type { ConnectedApp } from './types';

interface ErrorBody {
  error?: string;
  message?: string;
}

export interface ConnectedAppsError extends Error {
  status?: number;
  body?: ErrorBody;
}

function toError(stage: string, response: Response | undefined, error: unknown): ConnectedAppsError {
  const e = new Error(stage) as ConnectedAppsError;
  if (response) e.status = response.status;
  e.body = (error ?? {}) as ErrorBody;
  return e;
}

export interface ConnectedAppsPage {
  items: ConnectedApp[];
  next_cursor: string | null;
}

export const connectedAppsQueryKey = ['connected-apps', 'list'] as const;

const PAGE_SIZE = 20;

export function useConnectedApps() {
  const api = useApi();
  return useInfiniteQuery<ConnectedAppsPage, ConnectedAppsError>({
    queryKey: connectedAppsQueryKey,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const cursor = pageParam as string | undefined;
      const { data, error, response } = await api.GET('/auth/consents', {
        params: { query: { limit: PAGE_SIZE, ...(cursor ? { cursor } : {}) } },
      });
      if (error) throw toError('connected_apps_list_failed', response, error);
      const payload = data as { items?: unknown[]; next_cursor?: string | null } | undefined;
      return {
        items: (payload?.items ?? []) as ConnectedApp[],
        next_cursor: payload?.next_cursor ?? null,
      };
    },
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });
}

export function useRevokeConnectedApp() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<void, ConnectedAppsError, { id: string }>({
    mutationFn: async ({ id }) => {
      const { error, response } = await api.DELETE('/auth/consents/{id}', {
        params: { path: { id } },
      });
      if (error) throw toError('connected_app_revoke_failed', response, error);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: connectedAppsQueryKey });
    },
  });
}
